import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { RenderBlocks } from '@shared/block-renderer';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatDate, formatNumber, isUlidShaped, toMachineDate } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { ErrorBanner } from '@shared/ui/ErrorBanner';

import { deleteCopy } from '../_shared/copy';
import { EDITORIAL_MALFORMED_ID, editorialErrors } from '../_shared/errors';
import { useDeletePost, usePublishPost, useRejectPost } from '../_shared/mutations';
import { PostStateBadge } from '../_shared/PostStateBadge';
import { postKey, usePermissions, usePost } from '../_shared/queries';
import { RejectDialog } from '../_shared/RejectDialog';
import { STATE_ACTIONS } from '../_shared/states';
import { holdsReviewerRole, type EditorialPostDetail } from '../_shared/types';
import { EditorialDetailSkeleton } from './EditorialDetail.Skeleton';
import { copy } from './copy';

/**
 * One post rendered as the reader will see it, with the reviewer's decision
 * beside it. This is where the second pair of hands acts.
 *
 * **The decision is taken on the output, not on the inputs, and that is why
 * this route exists at all.** A reviewer looking at a form is judging fields; a
 * reviewer looking at the rendered article is judging what readers will get. So
 * the render uses the same registry the public renderer draws from, over the
 * same block tree, and it is capped at the reading measure — reviewing on a
 * column wider than the reader's is judging text nobody will see that way.
 *
 * **Neither decision takes the action colour.** Approve and reject are
 * opposites, and giving the accent to one tells the reviewer which is «right»
 * before they have read the article. The one-primary rule is satisfied here by
 * there being no primary at all.
 *
 * **The two decisions are absent for a caller without the granted reviewer
 * role**, never disabled — a disabled control promises a permission that is
 * coming, and this one is never coming to a writer. A holder viewing their own
 * post still meets the server's refusal, and its copy names the rule.
 */
export function EditorialDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  /**
   * A malformed address is answered here rather than by the service.
   *
   * `/editorial/not-a-ulid` cannot name a record, and the read for it returned
   * the same «لم يعد هذا المقال موجوداً» as a well-formed id for a post that had
   * genuinely been removed — so an operator following a broken link was told
   * their article was deleted. The query is not fired at all: there is nothing
   * for the service to look up.
   */
  const malformedId = !isUlidShaped(postId);
  const query = usePost(malformedId ? undefined : postId);
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const publishPost = usePublishPost();
  const rejectPost = useRejectPost();
  const deletePost = useDeletePost();

  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [writeError, setWriteError] = useState<unknown>(null);

  const post = query.data;
  const canReview = holdsReviewerRole(permissions.data);

  /**
   * What this post's state actually offers — the same table the queue's row menu
   * reads, so the two surfaces cannot disagree about one record.
   *
   * An unknown state degrades to no actions rather than to every action: the
   * vocabulary is the service's, and a state this bundle has not been taught is
   * one whose transitions it cannot vouch for.
   */
  const stateActions = post ? (STATE_ACTIONS[post.state] ?? []) : [];

  const confirmSubject: ConfirmSubject | null = post
    ? {
        heading: deleteCopy.heading(post.title),
        body: deleteCopy.body,
        commitLabel: deleteCopy.commit,
        intent: 'danger',
      }
    : null;

  return (
    <>
      <Breadcrumb
        items={[
          { label: copy.crumbs.root, to: '/' },
          { label: copy.crumbs.list, to: '/editorial' },
          { label: post?.title ?? copy.head.title },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {malformedId ? (
        <ErrorBanner message={editorialErrors[EDITORIAL_MALFORMED_ID]!.message} tone="neutral" />
      ) : null}

      {!malformedId && query.isError ? (
        <ErrorBanner
          {...resolve(query.error)}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: postKey(postId ?? '') })}
        />
      ) : null}

      {!malformedId && query.isPending ? <EditorialDetailSkeleton /> : null}

      {post ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
          <article className="min-w-0 max-w-measure [&>*]:mb-4">
            {/* The hero is decoration on this surface — the article's own
                title is the heading that names it. */}
            <span
              aria-hidden="true"
              className="mb-5 block aspect-[12/5] w-full rounded-sm border border-border bg-[linear-gradient(145deg,color-mix(in_srgb,var(--brand-noir)_72%,var(--brand-bone)_28%),color-mix(in_srgb,var(--brand-noir)_90%,var(--brand-bone)_10%)_55%,color-mix(in_srgb,var(--brand-noir)_80%,var(--brand-indigo)_20%))]"
            />

            {/* An `h2`, not an `h1`: the page's own head already carries the
                first level, and two of them in one document is the defect the
                heading rule exists to prevent. */}
            <h2 className="font-display text-h1 font-bold">{post.title}</h2>

            <p className="-mt-4 mb-5 flex flex-wrap gap-2 text-xs text-ink-muted">
              <span>{copy.byline.author(post.author.displayName)}</span>
              {bylineDate(post) ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    {post.publishedAt ? copy.byline.published : copy.byline.submitted}{' '}
                    <time dateTime={toMachineDate(bylineDate(post) as string)}>
                      {formatDate(bylineDate(post) as string)}
                    </time>
                  </span>
                </>
              ) : null}
              {post.estimatedReadingMinutes !== null ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    <bdi>{copy.byline.readingTime(formatNumber(post.estimatedReadingMinutes))}</bdi>
                  </span>
                </>
              ) : null}
            </p>

            <RenderBlocks blocks={post.body} />

            {post.tags.length > 0 ? (
              <nav
                aria-label={copy.tagsLabel}
                className="mt-6 flex flex-wrap items-center gap-2 border-t border-border pt-4"
              >
                <span className="text-xs text-ink-muted">{copy.tagsLead}</span>
                {post.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-pill border border-border bg-surface-overlay px-3 py-1 text-xs text-ink-muted"
                  >
                    {tag.name}
                  </span>
                ))}
              </nav>
            ) : null}
          </article>

          <aside aria-label={copy.railLabel} className="grid gap-4">
            <RailCard heading={copy.state.heading}>
              <p className="mb-2">
                <PostStateBadge state={post.state} />
              </p>
              <p className="mb-0 max-w-none text-xs text-ink-muted">
                {post.submittedAt
                  ? copy.state.submittedBy(post.author.displayName, formatDate(post.submittedAt))
                  : null}{' '}
                {post.state === 'PUBLISHED' ? copy.state.published : copy.state.notPublished}
              </p>

              {/* The reason appears on the page after the rejection: it is what
                  the writer will read, and hiding it leaves the reviewer unable
                  to see what they sent. */}
              {post.rejectionReason ? (
                <Alert kind="danger" title={copy.state.rejectedTitle} className="mt-4">
                  {post.rejectionReason}
                </Alert>
              ) : null}

              {canReview && post.state === 'AWAITING_REVIEW' ? (
                <div className="mt-5 rounded-md border border-border bg-bg-sunken p-4">
                  <p className="mb-3 max-w-none text-xs text-ink-muted">{copy.review.lead}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={publishPost.isPending}
                      onClick={() =>
                        publishPost.mutate(post.id, {
                          onSuccess: () => setAnnouncement(copy.announce.published),
                          onError: setWriteError,
                        })
                      }
                      className="flex-1 rounded-sm border border-border-strong bg-surface-raised px-5 py-3 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:opacity-45"
                    >
                      {copy.review.approve}
                    </button>
                    <button
                      type="button"
                      disabled={rejectPost.isPending}
                      onClick={() => setRejecting(true)}
                      className="flex-1 rounded-sm border border-danger px-5 py-3 text-sm font-semibold text-danger hover:bg-danger-soft disabled:opacity-45"
                    >
                      {copy.review.reject}
                    </button>
                  </div>
                  <p className="mb-0 mt-3 max-w-none text-xs text-ink-muted">{copy.review.hint}</p>
                </div>
              ) : null}
            </RailCard>

            <RailCard heading={copy.meta.heading}>
              {/*
                «What is missing» is answered before approval, not after —
                approval publishes immediately, so anything absent has to be
                caught here or corrected through a withdrawal.
              */}
              <dl className="m-0 mb-3 grid gap-2 text-sm sm:grid-cols-[max-content_minmax(0,1fr)] sm:gap-x-4">
                <dt className="text-ink-muted">{copy.meta.author}</dt>
                <dd className="m-0">
                  <bdi>{post.author.displayName}</bdi>
                </dd>
                <dt className="text-ink-muted">{copy.meta.tags}</dt>
                <dd className="m-0">
                  <bdi>{formatNumber(post.tags.length)}</bdi>
                </dd>
                <dt className="text-ink-muted">{copy.meta.thumbnail}</dt>
                <dd className="m-0">{post.thumbnailUrl ? copy.meta.present : copy.meta.missing}</dd>
                <dt className="text-ink-muted">{copy.meta.hero}</dt>
                <dd className="m-0">{post.heroArtworkUrl ? copy.meta.present : copy.meta.derived}</dd>
                <dt className="text-ink-muted">{copy.meta.related}</dt>
                <dd className="m-0">
                  <bdi>{formatNumber(post.relatedItems.length)}</bdi>
                </dd>
                <dt className="text-ink-muted">{copy.meta.blocks}</dt>
                <dd className="m-0">
                  <bdi>{formatNumber(post.body.length)}</bdi>
                </dd>
              </dl>
              <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.meta.hint}</p>
            </RailCard>

            {/*
              **The card is drawn from `STATE_ACTIONS`, not written out flat.**
              It offered *edit* and *delete* in every state, so the row menu in
              the queue and this card could disagree about the same record. The
              table is the single source: `PUBLISHED` offers no `edit`, and this
              card says exactly that.
            */}
            {stateActions.length > 0 ? (
              <RailCard heading={copy.otherActions.heading}>
                <div className="flex flex-wrap gap-2">
                  {stateActions.includes('edit') ? (
                    <Link
                      to={`/editorial/${post.id}/edit`}
                      className="flex flex-1 items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-5 py-3 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay"
                    >
                      {copy.otherActions.edit}
                    </Link>
                  ) : null}

                  {stateActions.includes('delete') ? (
                    <button
                      type="button"
                      disabled={deletePost.isPending}
                      onClick={() => setDeleting(true)}
                      className="flex-1 rounded-sm border border-danger px-5 py-3 text-sm font-semibold text-danger hover:bg-danger-soft disabled:opacity-45"
                    >
                      {copy.otherActions.delete}
                    </button>
                  ) : null}
                </div>
                <p className="mb-0 mt-3 max-w-none text-xs text-ink-muted">{copy.otherActions.hint}</p>
              </RailCard>
            ) : null}

            {writeError ? <ErrorBanner {...resolve(writeError)} /> : null}
          </aside>
        </div>
      ) : null}

      <RejectDialog
        open={rejecting}
        {...(post ? { authorName: post.author.displayName } : {})}
        busy={rejectPost.isPending}
        onReject={(reason) => {
          if (!post) return;
          setRejecting(false);
          rejectPost.mutate(
            { postId: post.id, reason },
            { onSuccess: () => setAnnouncement(copy.announce.rejected), onError: setWriteError },
          );
        }}
        onCancel={() => setRejecting(false)}
      />

      <ConfirmDialog
        open={deleting}
        subject={confirmSubject}
        busy={deletePost.isPending}
        onConfirm={() => {
          if (!post) return;
          setDeleting(false);
          deletePost.mutate(post.id, {
            onSuccess: () => {
              setAnnouncement(copy.announce.deleted);
              // The post this route is addressed by no longer exists, so staying
              // would leave the operator reading a record the next refetch 404s.
              void navigate('/editorial');
            },
            onError: setWriteError,
          });
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

/** Published date where there is one, the submission otherwise. */
function bylineDate(post: EditorialPostDetail): string | null {
  return post.publishedAt ?? post.submittedAt;
}

function RailCard({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="p-4">
        <h2 className="mb-2 font-display text-h4">{heading}</h2>
        {children}
      </div>
    </section>
  );
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, editorialErrors);

  return {
    message: resolved.message,
    tone: resolved.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
